import { defineStore } from 'pinia';
import { setSentryOrg } from '@/utils/sentry';
import { abilityGranted } from '@/utils/abilities';
import { clearOrgPreferences, setOrgPreferences } from '@/utils/orgPreferences';
import { resetOrgScopedCaches } from '@/utils/orgScopedCaches';

// In-flight request de-duplication and a per-session employee cache. Kept
// module-level (not reactive state) — they are transient plumbing, and reactivity
// would add cost for no benefit. When several components mount at once (e.g. the
// two QuickAdd bars plus the assignee filter all resolving the same employee, or
// both bars loading workday-start), they share a single GET instead of firing it
// two or three times. Cleared on org switch via clear().
const inFlight = new Map();
const employeeCache = new Map();
const projectMemberCache = new Map();

/**
 * Organization store - replaces window.Org and window.OrgApi
 * Manages current organization context
 */
export const useOrgStore = defineStore('org', {
    state: () => ({
        id: '',
        name: '',
        slug: '',
        logo: '',
        document_logo: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        postal_code: '',
        country: '',
        phones: [],
        websites: [],
        emails: [],
        currency_code: 'CAD',
        // The conventions this organization writes its numbers with. Not the
        // language, which stays each reader's own: the two join when a figure
        // is formatted.
        region: 'CA',
        default_language: '',
        // The organization's clock; '' when it was never set (UTC then applies).
        timezone: '',
        ending_month: 12,
        settings: {},
        apiBase: '',
        enabledModules: [],
        modulesLoaded: false,
        taskSettings: {},
        taskSettingsLoaded: false,
        smsSendability: null,
        smsSendabilityLoaded: false,
        workOrderSettings: {},
        workOrderSettingsLoaded: false,
        salesSettings: {},
        salesSettingsLoaded: false,
        projectSettings: {},
        projectSettingsLoaded: false,
        workdayStart: {},
        workdayStartLoaded: false,
        fiscalPeriods: [],
        fiscalQuarters: [],
        fiscalPeriodsLoaded: false,
        employees: [],
        employeesLoaded: false,
        hasPaymentGateway: false,
        userRoles: [],
        userAbilities: [],
        availableRoles: [],
        rolesLoaded: false,
        defaultWarehouse: null,
        theme_preset: '',
        org_type: '',
        // Org-wide: how lists lay themselves out — 'adaptive' (full-width table, narrow
        // list beside an opened record), 'narrow' (the classic layout at every moment)
        // or 'full' (full width at every moment; an opened record hides the list).
        listLayoutMode: 'adaptive',
        // Org-wide: when true, a record detail opens in Edit mode for users who can edit it.
        objectEditModeEnabled: false,
        // Org-wide (Settings ▸ General): seconds a toast alert stays on screen, 0 = never pop one.
        toastDurationSeconds: 5,
        // Same, for new mail alone. null = follow the general duration; 0 = never for mail.
        mailToastDurationSeconds: 5,
        // Org-wide: whether an alert arrives with a sound. Silence stops the sound alone.
        toastSoundEnabled: true,
        // Org-wide: lines a document line description may grow to, 0 = no limit.
        documentLineMaxRows: 0,
        // Org-wide (Settings ▸ CRM): whether an opportunity's comments and activities
        // are also read on the prospect or customer it belongs to.
        crmOpportunityFeedOnContact: true,
        // Org-wide (Settings ▸ CRM): days without activity after which a deal reads
        // as stalled. Held here for the settings screen only — the badge and the
        // filter both get their answer from the server, which owns the rule.
        crmStaleDays: 30,
        // Org-wide (Settings ▸ CRM): days without activity, with nothing planned,
        // after which a prospect reads « Sans suivi ». Same as above: the server
        // owns the rule, this copy is for the settings screen.
        crmProspectFollowUpDays: 30,
        // Org-wide (Settings ▸ CRM): the amount the « large deal » quick filter
        // means here. The chip itself is declared and counted by the server; this
        // copy exists so the settings screen can show the current value.
        crmLargeDeal: 100000,
        // Org-wide (Settings ▸ CRM): whether a contact filed under a company may
        // itself be a customer, supplier or prospect. Off by default — flagging a
        // person at a client is what put employees in the Clients list. Read here
        // by the fiche and the attach form to stop showing a refusal the server
        // would not make; the server still owns the rule.
        crmSubContactRolesAllowed: true,
        // Org-wide (Settings ▸ CRM ▸ General): whether a contact may be filed
        // under a person as well as under a company. Read by the attach forms
        // and the fiche's tabs; the server still owns the rule.
        crmIndividualParentsAllowed: false,
        // Org-wide (Settings ▸ CRM ▸ General): whether a deal may be split into
        // child deals. Read by the deal's « ⋮ » menu and its children tab; the
        // server still owns the rule.
        crmChildOpportunitiesAllowed: false,
        // Whether an invoice must be approved before it is issued (Settings ▸ Sales).
        invoiceApprovalRequired: false,
        // Org-wide (Settings ▸ Tasks): what a day and a week are worth when an
        // estimate is typed as « 2j » or read back as « 1 j 4 h ». The grammar
        // itself reads these through utils/orgPreferences, which cannot see a
        // store; this copy exists so the settings screen can show them.
        estimateHoursPerDay: 8,
        estimateDaysPerWeek: 5
    }),

    getters: {
        /**
         * Get the API base URL for current organization
         * Replaces window.OrgApi
         */
        api: state => {
            return state.apiBase;
        },

        /**
         * Check if an organization is selected
         */
        isSelected: state => {
            return !!state.id;
        },

        /**
         * Get organization setting by key
         */
        getSetting: state => {
            return (key, defaultValue = null) => {
                return state.settings[key] ?? defaultValue;
            };
        },

        /**
         * Whether the organization's plan includes a module.
         *
         * Answers `false` while the module list is still loading. Being
         * permissive there used to flash menu entries for modules the
         * organization had not bought, and a boundary that shows what it is
         * about to hide is not a boundary — the failure direction has to be
         * the safe one. The sidebar rebuilds when `enabledModules` arrives.
         */
        isModuleEnabled: state => {
            return slug => {
                if (!state.modulesLoaded) return false;
                return state.enabledModules.includes(slug);
            };
        },

        /**
         * Check if current user has a specific ability in this org.
         * Matching lives in `abilityGranted()`, which mirrors the server.
         */
        can: state => {
            return ability => {
                if (!state.rolesLoaded) return true; // permissive until loaded
                return abilityGranted(state.userAbilities, ability);
            };
        },

        /**
         * Check if user has any ability matching a prefix.
         * Used for sidebar gating (e.g., canAny('accounting') shows the section
         * whether the user has 'accounting', 'accounting.sales', or 'accounting.view').
         */
        canAny: state => {
            return prefix => {
                if (!state.rolesLoaded) return true;
                if (state.userAbilities.includes('*')) return true;
                return state.userAbilities.some(a => a === prefix || a.startsWith(prefix + '.'));
            };
        },

        hasRole: state => {
            return role => {
                if (!state.rolesLoaded) return true;
                return state.userRoles.includes(role);
            };
        },

        isOwner: state => state.userRoles.includes('owner'),

        isConstruction: state => {
            const professionalTypes = [
                'marketing_agency',
                'web_software',
                'accounting_firm',
                'consulting',
                'legal_services',
                'architecture_engineering',
                'design',
                'professional_other'
            ];
            return state.org_type !== '' && !professionalTypes.includes(state.org_type);
        },

        isAdmin: state => state.userRoles.includes('admin') || state.userRoles.includes('owner'),

        /**
         * Primary phone / website from the first entry of the org's contact arrays.
         * Matches the portal store's convention so document-box pages can read
         * the same fields regardless of context.
         */
        phone: state => state.phones?.[0]?.value || '',
        website: state => state.websites?.[0]?.value || '',

        /**
         * Multi-line address block used on document previews (name, street,
         * city/state/postal, website, phone).
         */
        orgAddress: state => {
            const parts = [];
            if (state.name) parts.push(state.name);
            if (state.address1) parts.push(state.address1);
            if (state.address2) parts.push(state.address2);
            if (state.city) {
                let line = state.city;
                if (state.state) line += ', ' + state.state;
                if (state.postal_code) line += ' ' + state.postal_code;
                parts.push(line);
            }
            const website = state.websites?.[0]?.value || '';
            if (website) parts.push(website);
            const phone = state.phones?.[0]?.value || '';
            if (phone) parts.push(phone);
            return parts.join('\n');
        }
    },

    actions: {
        /**
         * Initialize store from window.Org and window.OrgApi (for backward compatibility)
         */
        initFromLegacy() {
            if (typeof window !== 'undefined') {
                if (window.Org) {
                    this.id = window.Org.id || '';
                }
                if (window.OrgApi) {
                    this.apiBase = window.OrgApi;
                }
            }
        },

        /**
         * Set organization data
         * @param {Object} orgData - Organization data object
         */
        setOrg(orgData) {
            const orgChanged = this.id && this.id !== (orgData.id || '');

            this.id = orgData.id || '';
            this.name = orgData.name || '';
            this.slug = orgData.slug || '';
            this.logo = orgData.logo || '';
            this.document_logo = orgData.document_logo || '';
            this.address1 = orgData.address1 || '';
            this.address2 = orgData.address2 || '';
            this.city = orgData.city || '';
            this.state = orgData.state || '';
            this.postal_code = orgData.postal_code || '';
            this.country = orgData.country || '';
            this.phones = orgData.phones || [];
            this.websites = orgData.websites || [];
            this.emails = orgData.emails || [];
            this.currency_code = orgData.currency_code || 'CAD';
            this.default_language = orgData.default_language || '';
            this.timezone = orgData.timezone || '';
            this.ending_month = orgData.ending_month || 12;
            this.settings = orgData.settings || {};
            this.theme_preset = orgData.theme_preset || '';
            this.org_type = orgData.org_type || '';
            this.listLayoutMode = orgData.list_layout_mode || 'adaptive';
            this.objectEditModeEnabled = orgData.object_edit_mode_enabled ?? false;
            this.setPreferences(orgData);

            // Reset cached data when switching to a different org
            if (orgChanged) {
                this.enabledModules = [];
                this.modulesLoaded = false;
                this.userRoles = [];
                this.userAbilities = [];
                this.availableRoles = [];
                this.rolesLoaded = false;
                this.workdayStart = {};
                this.workdayStartLoaded = false;
                this.salesSettings = {};
                this.salesSettingsLoaded = false;
                this.projectSettings = {};
                this.projectSettingsLoaded = false;
                this.fiscalPeriods = [];
                this.fiscalQuarters = [];
                this.fiscalPeriodsLoaded = false;

                // Layouts, columns, saved views, grid preferences and filters are all
                // the organization's own.
                resetOrgScopedCaches();
            }

            // Set enabled modules from session data if available
            if (Array.isArray(orgData.enabled_modules)) {
                this.enabledModules = orgData.enabled_modules;
                this.modulesLoaded = true;
            }

            this.hasPaymentGateway = !!orgData.has_payment_gateway;

            setSentryOrg({ id: this.id, slug: this.slug });
        },

        /**
         * Set the API base URL
         * @param {String} apiBase - API base URL (e.g., '/api/org/123')
         */
        setApiBase(apiBase) {
            this.apiBase = apiBase;
        },

        /**
         * Switch to a different organization
         * @param {String} orgId - Organization ID to switch to
         */
        async switchOrg(orgId) {
            try {
                const response = await axios.get(`/api/organizations/${orgId}`);
                this.setOrg(response.data);
                this.setApiBase(`/api/org/${orgId}`);
                return response.data;
            } catch (error) {
                throw error;
            }
        },

        /**
         * Load current organization data
         */
        async load() {
            if (!this.id) return;

            try {
                const response = await axios.get(`${this.apiBase}/organization`);
                this.setOrg(response.data);
            } catch (error) {
                console.error('Failed to load organization data:', error);
            }
        },

        /**
         * Update organization settings
         * @param {Object} settings - Settings to update
         */
        async updateSettings(settings) {
            try {
                const response = await axios.put(`${this.apiBase}/settings`, settings);
                this.settings = { ...this.settings, ...response.data };
                return response.data;
            } catch (error) {
                throw error;
            }
        },

        /**
         * Load enabled modules (cached — only fetches once per org session)
         */
        async loadModules() {
            if (this.modulesLoaded) return this.enabledModules;
            if (!this.apiBase) return [];

            try {
                const response = await axios.get(`${this.apiBase}/settings/modules`);
                this.enabledModules = response.data.filter(m => m.enabled).map(m => m.slug);
                this.modulesLoaded = true;
                return this.enabledModules;
            } catch (error) {
                console.error('Failed to load modules:', error);
                return [];
            }
        },

        /**
         * Whether this organization can send an SMS at all, and what to say when
         * it cannot. Cached per org session: the composer asks on every fiche,
         * and the answer only changes when a number is bought or given back.
         */
        async loadSmsSendability() {
            if (this.smsSendabilityLoaded) return this.smsSendability;
            if (!this.apiBase) return null;

            try {
                const response = await axios.get(`${this.apiBase}/sms/sendability`);
                this.smsSendability = response.data;
                this.smsSendabilityLoaded = true;
            } catch (error) {
                // Not fatal: the send path answers for itself, and a refusal is
                // shown with its reason.
                this.smsSendability = null;
            }

            return this.smsSendability;
        },

        /**
         * Forget it, so the next composer asks again — after a number is bought,
         * or given back.
         */
        forgetSmsSendability() {
            this.smsSendability = null;
            this.smsSendabilityLoaded = false;
        },

        /**
         * Load task management settings (cached — only fetches once per org session)
         */
        async loadTaskSettings() {
            if (this.taskSettingsLoaded) return this.taskSettings;
            if (!this.apiBase) return {};

            try {
                const response = await axios.get(`${this.apiBase}/settings/tasks`);
                this.taskSettings = response.data;
                this.taskSettingsLoaded = true;
                return this.taskSettings;
            } catch (error) {
                console.error('Failed to load task settings:', error);
                return {};
            }
        },

        /**
         * Load work order settings (cached — only fetches once per org session)
         */
        async loadWorkOrderSettings() {
            if (this.workOrderSettingsLoaded) return this.workOrderSettings;
            if (!this.apiBase) return {};

            try {
                const response = await axios.get(`${this.apiBase}/work-order-settings`);
                this.workOrderSettings = response.data;
                this.workOrderSettingsLoaded = true;
                return this.workOrderSettings;
            } catch (error) {
                return {};
            }
        },

        /**
         * The org-wide interface preferences, from the organization payload or from
         * a save on Settings ▸ General. Kept in one place so both callers agree on
         * the defaults — and on the fact that a null mail duration is not a zero.
         *
         * @param {Object} data - Any object carrying the preference keys
         */
        setPreferences(data = {}) {
            this.toastDurationSeconds = Number(data.toast_duration_seconds ?? 5);
            // Undefined is a payload that never carried the key — the default applies.
            // Null is the org's own answer: follow the general duration.
            this.mailToastDurationSeconds =
                data.mail_toast_duration_seconds === undefined
                    ? 5
                    : data.mail_toast_duration_seconds === null
                      ? null
                      : Number(data.mail_toast_duration_seconds);
            this.toastSoundEnabled = data.toast_sound_enabled ?? true;
            this.documentLineMaxRows = Number(data.document_line_max_rows ?? 0);
            this.crmOpportunityFeedOnContact = data.crm_opportunity_feed_on_contact ?? true;
            this.crmStaleDays = Number(data.crm_stale_days ?? 30);
            this.crmProspectFollowUpDays = Number(data.crm_prospect_follow_up_days ?? 30);
            this.crmLargeDeal = Number(data.crm_large_deal ?? 100000);
            this.crmSubContactRolesAllowed = data.crm_sub_contact_roles_allowed ?? true;
            this.crmIndividualParentsAllowed = !!data.crm_individual_parents_allowed;
            this.crmChildOpportunitiesAllowed = !!data.crm_child_opportunities_allowed;
            this.invoiceApprovalRequired = !!data.invoice_approval_required;
            this.estimateHoursPerDay = Number(data.estimate_hours_per_day ?? 8);
            this.estimateDaysPerWeek = Number(data.estimate_days_per_week ?? 5);
            this.region = data.region || 'CA';

            // The currency travels with the preferences now that it is editable
            // there, but it also arrives on its own in the org payload — so an
            // absent key must not reset it to the default.
            if (data.currency_code) this.currency_code = data.currency_code;

            // What the organization is, for the leaves that format on its behalf
            // — its clock, its currency, its country, and what a day of work is
            // worth. They cannot reach a store, so the store reaches them (see
            // utils/orgPreferences). Pushed from here rather than from
            // setOrgData so a save on a settings screen takes effect at once,
            // instead of « 2j » meaning the old day until the next page load.
            setOrgPreferences({
                currency: this.currency_code,
                country: this.country,
                timezone: this.timezone,
                estimateHoursPerDay: this.estimateHoursPerDay,
                estimateDaysPerWeek: this.estimateDaysPerWeek
            });
        },

        /**
         * Load sales settings (cached for the session, de-duplicated while in flight).
         * A sales document form asks for them twice — the form itself for the quote
         * rules, its line editor for the description height — and both must cost one
         * request. Reset the flag after saving them in Settings ▸ Sales.
         */
        async loadSalesSettings() {
            if (this.salesSettingsLoaded) return this.salesSettings;
            if (!this.apiBase) return {};
            if (inFlight.has('salesSettings')) return inFlight.get('salesSettings');

            const request = axios
                .get(`${this.apiBase}/settings/sales`)
                .then(response => {
                    this.salesSettings = response.data || {};
                    this.salesSettingsLoaded = true;
                    return this.salesSettings;
                })
                .catch(() => ({}))
                .finally(() => inFlight.delete('salesSettings'));

            inFlight.set('salesSettings', request);
            return request;
        },

        /**
         * Load project settings (cached for the session, de-duplicated while in flight).
         *
         * Read by the route that decides which tab a project opens on, which runs
         * before anything is painted — so it is awaited there, not fired and hoped
         * for. Reset the flag after saving them in Settings ▸ Operations ▸ Projects.
         */
        async loadProjectSettings() {
            if (this.projectSettingsLoaded) return this.projectSettings;
            if (!this.apiBase) return {};
            if (inFlight.has('projectSettings')) return inFlight.get('projectSettings');

            const request = axios
                .get(`${this.apiBase}/project-settings`)
                .then(response => {
                    this.projectSettings = response.data || {};
                    this.projectSettingsLoaded = true;
                    return this.projectSettings;
                })
                .catch(() => ({}))
                .finally(() => inFlight.delete('projectSettings'));

            inFlight.set('projectSettings', request);
            return request;
        },

        /**
         * The organization's fiscal quarters and year, in dates.
         *
         * Computed by the server from the year-end month (Settings ▸
         * Accounting), so the dates a screen shows are the very ones the
         * `@this_quarter` filter resolves to — a second implementation in the
         * browser would drift the day either changed. Cached for the session:
         * a fiscal calendar does not move while someone reads a page.
         *
         * @returns {Promise<Array<{key: string, start: string, end: string, quarter: number}>>}
         */
        async loadFiscalPeriods() {
            if (this.fiscalPeriodsLoaded) return this.fiscalPeriods;
            if (!this.apiBase) return [];
            if (inFlight.has('fiscalPeriods')) return inFlight.get('fiscalPeriods');

            const request = axios
                .get(`${this.apiBase}/fiscal-periods`)
                .then(response => {
                    this.fiscalPeriods = response.data?.periods || [];
                    this.fiscalQuarters = response.data?.quarters || [];
                    this.fiscalPeriodsLoaded = true;
                    return this.fiscalPeriods;
                })
                .catch(() => [])
                .finally(() => inFlight.delete('fiscalPeriods'));

            inFlight.set('fiscalPeriods', request);
            return request;
        },

        /**
         * The organization's active task types, as `GET /task-types` orders them.
         *
         * Shared while in flight, not cached: the two quick-add bars of the
         * tasks list mount together and would each ask, but a type renamed or
         * switched off in Settings ▸ Tasks must show on the next screen opened,
         * not after a reload.
         *
         * @returns {Promise<Array>}
         */
        loadActiveTaskTypes() {
            if (!this.apiBase) return Promise.resolve([]);
            if (inFlight.has('activeTaskTypes')) return inFlight.get('activeTaskTypes');

            const request = axios
                .get(`${this.apiBase}/task-types`, { params: { active_only: 1 } })
                .then(response => response.data?.data ?? response.data ?? [])
                .catch(() => [])
                .finally(() => inFlight.delete('activeTaskTypes'));

            inFlight.set('activeTaskTypes', request);
            return request;
        },

        /**
         * Load workday start times per day (cached — only fetches once per org session)
         */
        async loadWorkdayStart() {
            if (this.workdayStartLoaded) return this.workdayStart;
            if (!this.apiBase) return {};
            // Two QuickAdd bars mount together and both call this before the
            // loaded flag flips — share the one request instead of racing.
            if (inFlight.has('workdayStart')) return inFlight.get('workdayStart');

            const request = axios
                .get(`${this.apiBase}/tasks/workday-start`)
                .then(response => {
                    this.workdayStart = response.data;
                    this.workdayStartLoaded = true;
                    return this.workdayStart;
                })
                .catch(() => ({}))
                .finally(() => inFlight.delete('workdayStart'));

            inFlight.set('workdayStart', request);
            return request;
        },

        /**
         * Load the unfiltered employee list, cached for the session and de-duplicated
         * while in flight. Every employee picker opens on this list, so a page holding
         * several of them (a filter, a bulk action, a quick-add bar) still costs one
         * request. Typed searches bypass the cache and go straight to the API.
         */
        async loadEmployees() {
            if (this.employeesLoaded) return this.employees;
            if (!this.apiBase) return [];
            if (inFlight.has('employees')) return inFlight.get('employees');

            const request = axios
                .get(`${this.apiBase}/employees`)
                .then(response => {
                    this.employees = response.data?.data || response.data || [];
                    this.employeesLoaded = true;
                    return this.employees;
                })
                .catch(() => [])
                .finally(() => inFlight.delete('employees'));

            inFlight.set('employees', request);
            return request;
        },

        /**
         * The users a project's work may be handed to: its own members.
         *
         * A task on a project assigned to somebody outside it is work nobody
         * follows — so every picker on a project screen reads this rather than
         * the organization's whole staff list.
         *
         * Cached per project and de-duplicated while in flight, which is the
         * whole reason it belongs in the store: a board column of twenty cards
         * each carrying an avatar picker would otherwise cost twenty identical
         * requests the first time somebody reassigns anything.
         */
        async loadProjectMembers(projectId) {
            if (!projectId || !this.apiBase) return [];

            const key = String(projectId);
            if (projectMemberCache.has(key)) return projectMemberCache.get(key);
            if (inFlight.has(`members:${key}`)) return inFlight.get(`members:${key}`);

            const request = axios
                .get(`${this.apiBase}/projects/${projectId}/members`)
                .then(response => {
                    const members = response.data?.data ?? response.data ?? [];
                    const users = members.map(member => member.user).filter(Boolean);

                    projectMemberCache.set(key, users);
                    return users;
                })
                // Not cached on failure, so a transient error can retry rather
                // than leaving every picker on the screen permanently empty.
                .catch(() => [])
                .finally(() => inFlight.delete(`members:${key}`));

            inFlight.set(`members:${key}`, request);
            return request;
        },

        /** Forget a project's members — call after adding or removing one. */
        forgetProjectMembers(projectId) {
            projectMemberCache.delete(String(projectId));
        },

        /**
         * Resolve a single employee by id, cached for the session and de-duplicated
         * while in flight. Several components on a list (the assignee filter and the
         * quick-add bars) resolve the same id on mount; this collapses them into one
         * request. Only successful lookups are cached, so a transient failure can retry.
         */
        async getEmployee(id) {
            if (!id || !this.apiBase) return null;
            const key = String(id);
            if (employeeCache.has(key)) return employeeCache.get(key);
            if (inFlight.has(`emp:${key}`)) return inFlight.get(`emp:${key}`);

            const request = axios
                .get(`${this.apiBase}/employees/${id}`)
                .then(response => {
                    const data = response.data?.id ? response.data : null;
                    if (data) employeeCache.set(key, data);
                    return data;
                })
                .catch(() => null)
                .finally(() => inFlight.delete(`emp:${key}`));

            inFlight.set(`emp:${key}`, request);
            return request;
        },

        /**
         * Load user roles & abilities (cached — only fetches once per org session)
         */
        async loadRoles() {
            if (this.rolesLoaded) return this.userRoles;
            if (!this.apiBase) return [];

            // `rolesLoaded` only flips once the answer is back, so a second
            // caller mounting meanwhile sent the same request again. Keyed by
            // org: a switch must not be handed the previous org's roles.
            const key = `roles:${this.apiBase}`;
            if (!inFlight.has(key)) {
                inFlight.set(
                    key,
                    this.fetchRoles().finally(() => inFlight.delete(key))
                );
            }

            return inFlight.get(key);
        },

        /**
         * Re-read the roles after they were changed on the server.
         *
         * Editing a role changes what its holders may do, so the answer in hand
         * is wrong the moment it is saved. What must NOT happen is dropping the
         * flag and waiting for someone else to fetch: the menus answer « nothing
         * yet » until the roles are known, so an invalidation nobody honours
         * empties the whole application until the next full reload. Hence the
         * swap — fetch, then replace — which also spares a blink on save.
         */
        async reloadRoles() {
            if (!this.apiBase) return [];

            return this.fetchRoles();
        },

        /** @private The one request behind both. */
        async fetchRoles() {
            try {
                const response = await axios.get(`${this.apiBase}/me/roles`);
                this.userRoles = response.data.roles;
                this.userAbilities = response.data.abilities;
                this.availableRoles = response.data.available_roles;
                this.defaultWarehouse = response.data.default_warehouse || null;
                this.rolesLoaded = true;
                return this.userRoles;
            } catch (error) {
                console.error('Failed to load roles:', error);
                return [];
            }
        },

        /**
         * Clear organization data
         */
        clear() {
            this.id = '';
            this.name = '';
            this.slug = '';
            this.logo = '';
            this.document_logo = '';
            this.default_language = '';
            this.timezone = '';
            clearOrgPreferences();
            this.theme_preset = '';
            this.org_type = '';
            this.settings = {};
            this.apiBase = '';
            this.enabledModules = [];
            this.modulesLoaded = false;
            this.taskSettings = {};
            this.taskSettingsLoaded = false;
            this.smsSendability = null;
            this.smsSendabilityLoaded = false;
            this.workOrderSettings = {};
            this.workOrderSettingsLoaded = false;
            this.salesSettings = {};
            this.salesSettingsLoaded = false;
            this.projectSettings = {};
            this.projectSettingsLoaded = false;
            this.setPreferences({});
            this.workdayStart = {};
            this.workdayStartLoaded = false;
            this.employees = [];
            this.employeesLoaded = false;
            this.userRoles = [];
            this.userAbilities = [];
            this.availableRoles = [];
            this.rolesLoaded = false;
            inFlight.clear();
            employeeCache.clear();
        }
    }
});
